import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { X, User, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NouvelleConversationModal({ isOpen, onClose, onConversationCreated }) {
  const { getAuthHeader, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen]);

  // Helper pour normaliser les réponses (parfois array direct, parfois { items|members|experts|coopers: [...] })
  const normalizeList = (resp, keys = []) => {
    const d = resp?.data;
    if (Array.isArray(d)) return d;
    if (d && typeof d === 'object') {
      for (const k of keys) {
        if (Array.isArray(d[k])) return d[k];
      }
      // Fallback : prend la première propriété qui est un array
      for (const v of Object.values(d)) {
        if (Array.isArray(v)) return v;
      }
    }
    return [];
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // Selon le rôle de l'utilisateur, charger différentes listes
      if (user.role === 'admin' || user.role === 'super_admin') {
        // Admin peut contacter experts, COOPER'S et membres.
        // Chaque appel a son propre catch pour éviter qu'un seul échec ne casse tout.
        const [expertsRes, coopersRes, membresRes] = await Promise.all([
          axios.get(`${API}/experts`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
          axios.get(`${API}/coopers`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
          axios.get(`${API}/membres`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        ]);

        const experts = normalizeList(expertsRes, ['experts', 'items', 'data']);
        const coopers = normalizeList(coopersRes, ['coopers', 'items', 'data']);
        const membres = normalizeList(membresRes, ['membres', 'members', 'items', 'data']);

        const allUsers = [
          ...experts.map((e) => ({ ...e, role: 'expert' })),
          ...coopers.map((c) => ({ ...c, role: 'cooper' })),
          ...membres.map((m) => ({ ...m, role: 'member' })),
        ];
        setUsers(allUsers);
      } else if (user.role === 'expert') {
        // Expert peut contacter admin, ses membres (liés par dommages) et COOPER'S
        const [membresRes, coopersRes] = await Promise.all([
          axios.get(`${API}/experts/mes-membres`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
          axios.get(`${API}/coopers`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        ]);
        const membres = normalizeList(membresRes, ['membres', 'members', 'items', 'data']);
        const coopers = normalizeList(coopersRes, ['coopers', 'items', 'data']);
        const allUsers = [
          { id: 'admin', nom: 'COPPAM', prenom: 'Administration', email: 'admin@coppam.fr', role: 'admin' },
          ...membres.map((m) => ({ ...m, role: 'member' })),
          ...coopers.map((c) => ({ ...c, role: 'cooper' })),
        ];
        setUsers(allUsers);
      } else if (user.role === 'member') {
        // Membre peut contacter admin, experts validés et son COOPER'S
        // V36.50: utilise /experts/contacts (accessible aux non-admins, sans param accentué)
        const [expertsRes, meRes] = await Promise.all([
          axios.get(`${API}/experts/contacts`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
          axios.get(`${API}/members/me`, { headers: getAuthHeader() }).catch(() => ({ data: null })),
        ]);
        const experts = normalizeList(expertsRes, ['experts', 'items', 'data']);
        const allUsers = [
          { id: 'admin', nom: 'COPPAM', prenom: 'Administration', email: 'admin@coppam.fr', role: 'admin' },
          ...experts.map((e) => ({ ...e, role: 'expert' })),
        ];
        // Ajouter son COOPER'S si disponible
        const cooperId = meRes?.data?.cooper_id;
        if (cooperId) {
          try {
            const cooperRes = await axios.get(`${API}/coopers/${cooperId}`, { headers: getAuthHeader() });
            if (cooperRes?.data) allUsers.push({ ...cooperRes.data, role: 'cooper' });
          } catch { /* ignore */ }
        }
        setUsers(allUsers);
      } else if (user.role === 'cooper') {
        // COOPER'S peut contacter admin, super_admin, experts et ses membres
        // V36.50: utilise /experts/contacts (accessible aux non-admins, sans param accentué)
        const [membresRes, expertsRes] = await Promise.all([
          axios.get(`${API}/coopers/mes-membres`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
          axios.get(`${API}/experts/contacts`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        ]);
        const membres = normalizeList(membresRes, ['membres', 'members', 'items', 'data']);
        const experts = normalizeList(expertsRes, ['experts', 'items', 'data']);
        const allUsers = [
          { id: 'admin', nom: 'COPPAM', prenom: 'Administration', email: 'admin@coppam.fr', role: 'admin' },
          { id: 'super_admin', nom: "O'SCOP", prenom: 'Super Admin', email: 'superadmin@oscop.fr', role: 'super_admin' },
          ...experts.map((e) => ({ ...e, role: 'expert' })),
          ...membres.map((m) => ({ ...m, role: 'member' })),
        ];
        setUsers(allUsers);
      }
    } catch {
      toast.error('Erreur lors du chargement des contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConversation = async () => {
    if (!selectedUser) {
      toast.error('Veuillez sélectionner un destinataire');
      return;
    }

    try {
      setLoading(true);

      // Déterminer le type de conversation
      let typeConversation = 'admin_expert';
      if ((user.role === 'admin' || user.role === 'super_admin') && selectedUser.role === 'expert') {
        typeConversation = 'admin_expert';
      } else if ((user.role === 'admin' || user.role === 'super_admin') && selectedUser.role === 'member') {
        typeConversation = 'admin_membre';
      } else if ((user.role === 'admin' || user.role === 'super_admin') && selectedUser.role === 'cooper') {
        typeConversation = 'admin_cooper';
      } else if (user.role === 'expert' && selectedUser.role === 'member') {
        typeConversation = 'expert_membre';
      } else if (user.role === 'expert' && selectedUser.role === 'cooper') {
        typeConversation = 'expert_cooper';
      } else if (user.role === 'expert' && (selectedUser.role === 'admin' || selectedUser.role === 'super_admin')) {
        typeConversation = 'admin_expert';
      } else if (user.role === 'member' && selectedUser.role === 'expert') {
        typeConversation = 'expert_membre';
      } else if (user.role === 'member' && (selectedUser.role === 'admin' || selectedUser.role === 'super_admin')) {
        typeConversation = 'admin_membre';
      } else if (user.role === 'member' && selectedUser.role === 'cooper') {
        typeConversation = 'cooper_membre';
      } else if (user.role === 'cooper' && selectedUser.role === 'member') {
        typeConversation = 'cooper_membre';
      } else if (user.role === 'cooper' && (selectedUser.role === 'admin' || selectedUser.role === 'super_admin')) {
        typeConversation = 'admin_cooper';
      }

      const response = await axios.post(
        `${API}/messagerie/conversations`,
        {
          type_conversation: typeConversation,
          participant1_id: user.id,
          participant1_role: user.role,
          participant2_id: selectedUser.id,
          participant2_role: selectedUser.role
        },
        { headers: getAuthHeader() }
      );

      toast.success('Conversation créée');
      onConversationCreated(response.data);
      onClose();
    } catch {
      toast.error('Erreur lors de la création de la conversation');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      u.nom?.toLowerCase().includes(searchLower) ||
      u.prenom?.toLowerCase().includes(searchLower) ||
      u.email?.toLowerCase().includes(searchLower) ||
      u.nom_structure?.toLowerCase().includes(searchLower) ||
      u.raison_sociale?.toLowerCase().includes(searchLower) ||
      u.entreprise?.toLowerCase().includes(searchLower)
    );
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">Nouvelle conversation</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4">
          <div className="mb-4">
            <Label>Rechercher un contact</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Nom, prénom, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Liste des utilisateurs */}
          <div className="max-h-64 overflow-y-auto space-y-2">
            {loading ? (
              <div className="flex justify-center py-4 md:py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredUsers.length === 0 ? (
              <p className="text-center text-slate-500 py-4 md:py-8">Aucun contact disponible</p>
            ) : (
              filteredUsers.map((u) => (
                <div
                  key={u.id}
                  onClick={() => setSelectedUser(u)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedUser?.id === u.id
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">
                        {u.nom_structure || u.raison_sociale || `${u.prenom || ''} ${u.nom || ''}`.trim() || u.entreprise || 'Utilisateur'}
                      </p>
                      <p className="text-sm text-slate-600">{u.email}</p>
                      <span className="text-xs text-slate-500 uppercase">{u.role}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-4 border-t border-slate-200">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Annuler
          </Button>
          <Button
            onClick={handleCreateConversation}
            disabled={!selectedUser || loading}
            className="flex-1 bg-blue-600 hover:bg-blue-700"
          >
            Créer
          </Button>
        </div>
      </div>
    </div>
  );
}
